import { Quote } from '@/model/quotesModel';
import { parseInflationPercent } from '@/utils/inflationParser';
import { parseDurationYears as parseBenefitPeriod } from '@/utils/benefitPeriod';
import { isOneAmericaAssetCare } from '@/util/oneAmericaPricingUtils';
import { quoteSupportsBenefitPaymentMode } from '@shared';
import type { BenefitPaymentType, InflationType } from '@/features/shop-insurance/types';

export const MAX_DURATION_YEARS = 9;

export function matchesTab(quote: Quote, tab: ShopInsuranceTab): boolean {
  if (tab === 'joint') return quote.policyType === 'Joint Life';
  // The Couples Discount tab was removed — its quotes are folded into the Single tab.
  return (
    quote.policyType === 'Single Life' ||
    quote.policyType === 'Standard' ||
    quote.policyType === 'Couples Discount'
  );
}

/**
 * Couples Discount quotes are single-insured quotes priced with a spousal
 * discount; they're folded into the Single tab and shown by default. When the
 * toggle is turned off, exclude them from the results.
 */
export function matchesCouplesDiscount(quote: Quote, includeCouplesDiscount: boolean): boolean {
  if (includeCouplesDiscount) return true;
  return quote.policyType !== 'Couples Discount';
}

export function parseDurationYearsAsNumber(value?: string): number {
  const result = parseBenefitPeriod(value);
  return result === 'lifetime' ? Infinity : result;
}

/**
 * Checks if a numeric value falls within a min-max range.
 *
 * Special case: Infinity (representing "Lifetime" duration) is treated as
 * within range if the max value equals the slider's maximum setting.
 * This allows users to include/exclude Lifetime products by adjusting the slider.
 */
export function inRange(value: number, min: number, max: number, sliderMax?: number): boolean {
  if (value === Infinity) return sliderMax !== undefined && max >= sliderMax;
  return value >= min && value <= max;
}

export function getInflationProtection(quote: Quote) {
  const hasSingle = !!quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None';

  if (hasSingle) {
    const rate = parseInflationPercent(quote.inflationBenefitOption);
    return { hasSingle: true, single: rate, aob: rate, cob: rate };
  }

  return {
    hasSingle: false,
    single: undefined as number | undefined,
    aob: parseInflationPercent(quote.aobInflation),
    cob: parseInflationPercent(quote.cobInflation),
  };
}

/**
 * Inflation protection filters on a minimum, not a range: a quote qualifies when the best
 * inflation rate it offers reaches the threshold. A OneAmerica policy at 0% AOB / 5% COB
 * clears a 3% filter on the strength of its COB rate, and single-rate products (Simple or
 * Compound) are measured against the same threshold rather than excluded.
 */
export function matchesMinimumInflation(quote: Quote, minimumPercent: number): boolean {
  if (minimumPercent <= 0) return true;
  const { aob, cob } = getInflationProtection(quote);
  return Math.max(aob, cob) >= minimumPercent;
}

export function getBenefitsDuration(quote: Quote) {
  const aobYears = parseDurationYearsAsNumber(quote.aobDuration);
  const cobYears = parseDurationYearsAsNumber(quote.cobDuration);

  if (aobYears || cobYears) {
    const totalYears =
      aobYears === Infinity || cobYears === Infinity ? Infinity : aobYears + cobYears;
    return { aobYears, cobYears, totalYears };
  }

  const totalYears = (quote.ltcBenefitDurationMonths ?? 0) / 12;
  return { aobYears: 0, cobYears: 0, totalYears };
}

export function hasInflationProtection(quote: Quote): boolean {
  if (quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None') {
    return parseInflationPercent(quote.inflationBenefitOption) > 0;
  }
  if (quote.aobInflation && quote.aobInflation !== 'None' && quote.aobInflation !== '0')
    return true;
  if (quote.cobInflation && quote.cobInflation !== 'None' && quote.cobInflation !== '0')
    return true;
  return false;
}

export function getQuoteInflationType(quote: Quote): InflationType | null {
  if (isOneAmericaAssetCare(quote.fundingPolicyType)) return 'compound';
  const label = quote.inflationBenefitOption;
  if (!label || label === 'None') return null;
  const lower = label.toLowerCase();
  if (lower.includes('simple')) return 'simple';
  if (lower.includes('compound')) return 'compound';
  return null;
}

export function matchesInflationType(quote: Quote, inflationTypes: InflationType[]): boolean {
  if (!hasInflationProtection(quote)) return false;
  const type = getQuoteInflationType(quote);
  if (type === null) return false;
  return inflationTypes.includes(type);
}

export function matchesBenefitPaymentType(
  quote: Quote,
  filter: BenefitPaymentType | 'all',
): boolean {
  if (filter === 'all') return true;
  return quoteSupportsBenefitPaymentMode(quote, filter);
}
